// Shared helpers for rich-text email content.
//
// Text / personalized blocks now store their content as HTML produced by the
// RichTextEditor (inline <b>, <font>, <span style="..."> tags for bold, font
// and color). Both the live preview (EmailBlockRenderer) and the sent email
// (renderEmailHtml) must turn that content into markup the SAME way, otherwise
// the WYSIWYG promise breaks — what the user styles must be what gets mailed.

export function esc(str: string): string {
  return (str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Strip executable / remote-loading constructs from authored HTML. Deliberately
// conservative — we allow styling + layout but block anything that runs code or
// loads remote scripts. Shared by the rich-text blocks and the raw-HTML block.
export function sanitizeEmailHtml(raw: string): string {
  return styleEmailLists(
    (raw || '')
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
      .replace(/<object[\s\S]*?<\/object>/gi, '')
      .replace(/<embed[\s\S]*?>/gi, '')
      .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
      .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
      .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
      .replace(/javascript:/gi, ''),
  );
}

// Pasted lists (from Gmail/Word) often arrive with `padding:0;margin:0`. With
// `list-style-position:outside` their markers then render in the zero-width
// gutter to the side of the list — which the email card's rounded
// `overflow:hidden` clips, so the numbers/bullets visually disappear. We force
// a guaranteed marker gutter on every <ol>/<ul>: `padding-inline-start` for
// direction-aware clients (Gmail/Apple — works for both LTR and RTL lists) and
// physical `padding-left` for Outlook (no logical-property support). Appended
// last so it overrides any inline `padding:0` the pasted markup carried.
const LIST_GUTTER_CSS = 'margin:8px 0;padding-inline-start:28px;padding-left:28px;list-style-position:outside;';

export function styleEmailLists(html: string): string {
  return (html || '').replace(/<(ol|ul)\b([^>]*)>/gi, (_m, tag: string, attrs: string) => {
    if (/\bstyle\s*=\s*"/i.test(attrs)) {
      return `<${tag}${attrs.replace(/style\s*=\s*"([^"]*)"/i, (_mm, s: string) => `style="${s};${LIST_GUTTER_CSS}"`)}>`;
    }
    return `<${tag}${attrs} style="${LIST_GUTTER_CSS}">`;
  });
}

// Strip baked-in *direction* (not alignment) from rich-text content. The editor
// has NO direction control, so any `dir` attr or `direction:` CSS inside the
// content is always unintentional paste residue (e.g. copied from a right-to-
// left Hebrew doc). That residue overrides the block's dir="auto" and forces
// English to render RTL on strict clients like Android Gmail. Removing it lets
// the block-level dir="auto" pick direction from the content.
//
// We KEEP intentional `text-align:left` / `text-align:center`: the editor
// exposes alignment buttons (justifyLeft/Center/Right), so those are real author
// choices, and stripping them would both discard the choice and desync the
// email from the on-canvas preview. We DO strip `text-align:right`, because in
// this RTL-default app "right" is never a meaningful choice — it's either paste
// residue (copied from a right-aligned Hebrew doc) or redundant with the
// default, and it's exactly what pins English to the wrong edge. Once removed,
// the block's logical `start` alignment governs and follows dir="auto":
// English -> left, Hebrew -> right.
//
// NOT applied to the raw-HTML block, where the author's own dir/align is
// intentional.
export function stripInlineDirection(html: string): string {
  return (html || '')
    // Drop dir="rtl" / dir='ltr' / dir=auto attributes wherever they appear.
    .replace(/\sdir\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    // Drop `direction:` declarations from inline styles.
    .replace(/direction\s*:\s*[^;"']+\s*;?/gi, '')
    // Drop only `text-align:right` (residue / redundant); keep left & center.
    .replace(/text-align\s*:\s*right\s*;?/gi, '');
}

// Heuristic: does this string contain HTML markup (vs. plain text)? Everything
// the RichTextEditor emits has tags; blocks saved before rich text existed are
// plain text with newlines. This keeps old campaigns rendering correctly.
export function looksLikeHtml(s: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(s || '');
}

// Turn block content into email-ready HTML: authored HTML is sanitized and
// passed through as-is; legacy plain text is escaped and its newlines become
// <br/>. Used by both the preview and the sent email so they always match.
export function renderRichText(content: string): string {
  const c = content || '';
  return looksLikeHtml(c)
    ? stripInlineDirection(sanitizeEmailHtml(c))
    : esc(c).replace(/\n/g, '<br/>');
}
